import React, { useState } from 'react';
import { Box, Typography, Snackbar, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api, { authService } from '../services/api';

const IOS = {
  bg:'#F2F2F7', surface:'#FFFFFF',
  separator:'rgba(60,60,67,0.12)',
  label:'#1C1C1E', label2:'rgba(60,60,67,0.55)',
  blue:'#007AFF', purple:'#5E5CE6', green:'#34C759',
  red:'#FF3B30', orange:'#FF9500',
};

// ── Fuera del componente para que no se recree en cada render ────────────────
const PasswordField = ({ label, value, onChange, show, onToggle, hint }) => (
  <Box sx={{ mb: hint ? 1 : 2, "& input::-ms-reveal":{display:"none"}, "& input::-ms-clear":{display:"none"} }}>
    <Typography sx={{ fontSize:'0.65rem', fontWeight:700, color:'rgba(255,255,255,0.5)', letterSpacing:'0.1em', textTransform:'uppercase', mb:0.8 }}>
      {label}
    </Typography>
    <Box sx={{ position:'relative' }}>
      <Box sx={{ position:'absolute', left:14, top:'50%', transform:'translateY(-50%)', pointerEvents:'none', display:'flex', alignItems:'center' }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="2" strokeLinecap="round">
          <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
      </Box>
      <input
        aria-label={label}
        type={show ? 'text' : 'password'}
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
          width:'100%', height:48, padding:'0 48px 0 44px',
          border:'1.5px solid rgba(255,255,255,0.18)', borderRadius:12,
          fontSize:'0.9rem', color:'white',
          backgroundColor:'rgba(255,255,255,0.10)',
          outline:'none', boxSizing:'border-box',
          fontFamily:'inherit', transition:'all 0.2s',
        }}
        onFocus={e=>{ e.target.style.borderColor='rgba(255,255,255,0.6)'; e.target.style.backgroundColor='rgba(255,255,255,0.14)'; }}
        onBlur={e=>{ e.target.style.borderColor='rgba(255,255,255,0.18)'; e.target.style.backgroundColor='rgba(255,255,255,0.10)'; }}
      />
      <Box component="button" type="button" aria-label={show ? "Ocultar contraseña" : "Mostrar contraseña"} aria-pressed={show} onMouseDown={e=>e.preventDefault()} onClick={onToggle}
        sx={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)',
          bgcolor:'transparent', border:'none', cursor:'pointer', display:'flex', alignItems:'center',
          p:0.5, borderRadius:'50%', '&:hover':{ bgcolor:'rgba(255,255,255,0.1)' } }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="2" strokeLinecap="round">
          {show
            ? <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></>
            : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>
          }
        </svg>
      </Box>
    </Box>
    {hint && <Typography sx={{ fontSize:'0.7rem', color:'rgba(255,255,255,0.35)', mt:0.6, ml:0.5, mb:1.5 }}>{hint}</Typography>}
  </Box>
);

// ── Componente principal ─────────────────────────────────────────────────────
const CambioPasswordObligatorio = () => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();

  const [passwordActual,    setPasswordActual]    = useState('');
  const [nuevaPassword,     setNuevaPassword]     = useState('');
  const [confirmarPassword, setConfirmarPassword] = useState('');
  const [showActual,        setShowActual]        = useState(false);
  const [showNueva,         setShowNueva]         = useState(false);
  const [showConfirmar,     setShowConfirmar]     = useState(false);
  const [cargando,          setCargando]          = useState(false);
  const [msg,               setMsg]               = useState({ open:false, text:'', severity:'info' });

  const showMsg = (text, severity) => setMsg({ open:true, text, severity });

  const handleCambiar = async () => {
    if (!passwordActual || !nuevaPassword || !confirmarPassword) { showMsg('Todos los campos son obligatorios','warning'); return; }
    if (nuevaPassword.length < 8 || nuevaPassword.length > 128) { showMsg('La nueva contraseña debe tener entre 8 y 128 caracteres','warning'); return; }
    if (nuevaPassword !== confirmarPassword) { showMsg('Las contraseñas no coinciden','error'); return; }
    if (passwordActual === nuevaPassword) { showMsg('La nueva contraseña debe ser diferente a la actual','warning'); return; }
    setCargando(true);
    try {
      const r = await api.post('/Auth/cambiar-password-obligatorio', { passwordActual, nuevaPassword });
      if (r.data.success) {
        localStorage.removeItem('token'); localStorage.removeItem('user'); localStorage.removeItem('password_change_required');
        showMsg('Contraseña actualizada. Inicia sesión con tu nueva clave.','success');
        setTimeout(()=>navigate('/login', {replace:true}),1500);
      }
    } catch(err) { showMsg(err.response?.data?.message||'Error al cambiar contraseña','error'); }
    finally { setCargando(false); }
  };

  return (
    <>
      <Box sx={{
        minHeight:'100vh', width:'100vw', position:'relative',
        backgroundImage:'url(/imagenes/gato_ardata.png)',
        backgroundSize:'cover', backgroundPosition:'center', backgroundRepeat:'no-repeat',
        display:'flex', alignItems:'center',
        justifyContent:{ xs:'center', md:'flex-start' },
        pl:{ xs:0, md:'6vw' }, px:{ xs:2, sm:0 },
      }}>
        {/* Overlay */}
        <Box sx={{
          position:'absolute', inset:0, zIndex:1,
          background:{
            xs:'rgba(0,0,0,0.52)',
            md:'linear-gradient(105deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.48) 42%, rgba(0,0,0,0.12) 100%)'
          }
        }} />

        {/* Panel */}
        <Box sx={{ position:'relative', zIndex:2, width:{ xs:'100%', sm:400 }, maxWidth:{ xs:420, sm:400 } }}>

          {/* Logo */}
          <Box sx={{ mb:4 }}>
            <img src="/imagenes/logo-integra.png" alt="integra"
              style={{ height:36, display:'block' }}
              onError={e=>e.target.style.display='none'} />
          </Box>

          {/* Título */}
          <Box sx={{ mb:4 }}>
            <Typography sx={{ fontSize:{ xs:'1.9rem', sm:'2.2rem' }, fontWeight:800, color:'white', lineHeight:1.15, letterSpacing:'-0.03em' }}>
              Cambio de
            </Typography>
            <Typography sx={{ fontSize:{ xs:'1.9rem', sm:'2.2rem' }, fontWeight:800, color:IOS.orange, lineHeight:1.15, letterSpacing:'-0.03em' }}>
              contraseña.
            </Typography>
            <Typography sx={{ fontSize:'0.86rem', color:'rgba(255,255,255,0.5)', mt:1.2 }}>
              Tu contraseña fue restablecida por un administrador o solicitaste recuperarla
            </Typography>
          </Box>

          {/* Chip usuario */}
          <Box sx={{ display:'inline-flex', alignItems:'center', gap:1, px:1.5, py:0.8, mb:3.5,
            borderRadius:'12px', bgcolor:'rgba(255,149,0,0.15)', border:'1px solid rgba(255,149,0,0.3)' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={IOS.orange} strokeWidth="2" strokeLinecap="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
            </svg>
            <Typography sx={{ fontSize:'0.82rem', fontWeight:700, color:IOS.orange }}>
              {user?.usuario || 'Usuario'}
            </Typography>
          </Box>

          {/* Formulario */}
          <form onSubmit={e=>{ e.preventDefault(); handleCambiar(); }}>
            <PasswordField label="Contraseña actual"
              value={passwordActual} onChange={setPasswordActual}
              show={showActual} onToggle={()=>setShowActual(v=>!v)} />

            <PasswordField label="Nueva contraseña"
              value={nuevaPassword} onChange={setNuevaPassword}
              show={showNueva} onToggle={()=>setShowNueva(v=>!v)}
              hint="Entre 8 y 128 caracteres" />

            <PasswordField label="Confirmar nueva contraseña"
              value={confirmarPassword} onChange={setConfirmarPassword}
              show={showConfirmar} onToggle={()=>setShowConfirmar(v=>!v)} />

            <Box sx={{ mt:1 }}>
              <Box component="button" type="submit" disabled={cargando}
                sx={{
                  width:'100%', height:50, borderRadius:'14px', border:'none',
                  bgcolor:cargando?'rgba(255,255,255,0.15)':IOS.orange,
                  cursor:cargando?'default':'pointer',
                  display:'flex', alignItems:'center', justifyContent:'center', gap:1,
                  transition:'all 0.2s',
                  boxShadow:cargando?'none':`0 8px 24px ${IOS.orange}50`,
                  '&:hover':{ filter:cargando?'none':'brightness(1.1)', transform:cargando?'none':'translateY(-1px)' },
                }}>
                {cargando && (
                  <Box sx={{ width:18, height:18, borderRadius:'50%',
                    border:'2px solid rgba(255,255,255,0.3)', borderTopColor:'white',
                    animation:'spin 0.8s linear infinite',
                    '@keyframes spin':{ '0%':{ transform:'rotate(0deg)' }, '100%':{ transform:'rotate(360deg)' } }
                  }} />
                )}
                <Typography sx={{ fontSize:'0.92rem', fontWeight:700, color:cargando?'rgba(255,255,255,0.5)':'white' }}>
                  {cargando ? 'Cambiando...' : 'Cambiar contraseña'}
                </Typography>
              </Box>
            </Box>
          </form>

          {/* Línea divisoria */}
          <Box sx={{ mt:5, display:'flex', alignItems:'center', gap:1.5 }}>
            <Box sx={{ flex:1, height:'1px', bgcolor:'rgba(255,255,255,0.1)' }} />
            <Typography sx={{ fontSize:'0.68rem', color:'rgba(255,255,255,0.25)', fontWeight:500 }}>Un bit a la vez.</Typography>
            <Box sx={{ flex:1, height:'1px', bgcolor:'rgba(255,255,255,0.1)' }} />
          </Box>
        </Box>

        {/* Logo ARDATA */}
        <Box sx={{ position:'fixed', bottom:{ xs:12, sm:20 }, right:{ xs:16, sm:24 }, zIndex:10,
          textAlign:'right', display:'flex', flexDirection:'column', alignItems:'flex-end', gap:0.5 }}>
          <img src="/imagenes/logo-ardata.png" alt="ARDATA"
            style={{ width:window.innerWidth<600?65:82, height:'auto', opacity:0.5 }}
            onError={e=>e.target.style.display='none'} />
          <Typography sx={{ fontSize:{ xs:'7px', sm:'8px' }, color:'rgba(255,255,255,0.3)' }}>
            © 2026 ardata, todos los derechos reservados
          </Typography>
        </Box>
      </Box>

      <Snackbar open={msg.open} autoHideDuration={6000} onClose={()=>setMsg(m=>({...m,open:false}))} anchorOrigin={{ vertical:'top', horizontal:'center' }}>
        <Alert onClose={()=>setMsg(m=>({...m,open:false}))} severity={msg.severity} variant="filled" sx={{ width:'100%' }}>
          {msg.text}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CambioPasswordObligatorio;
